import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { Navigate, useLocation } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import type { PanelApi } from '@iptv/api-client';
import type { Permission, Principal, TwoFactorChallenge } from '@iptv/shared-types';
import { Spinner } from '../components/primitives.js';

export type PanelAudience = 'merchant' | 'admin';

export function principalFitsAudience(p: Principal, audience: PanelAudience): boolean {
  return audience === 'admin'
    ? p.role === 'SUPER_ADMIN'
    : p.role !== 'SUPER_ADMIN' && p.tenantId !== null;
}

interface AuthState {
  api: PanelApi;
  audience: PanelAudience;
  user: Principal | null;
  /** True until the refresh cookie has been tried on page load. */
  restoring: boolean;
  /** Set when the server ended the session (revoked, expired) rather than the user signing out. */
  sessionLost: boolean;
  /** Signs in, or returns the two-step challenge to finish with `verifySignIn`. */
  signIn: (email: string, password: string) => Promise<Principal | TwoFactorChallenge>;
  /**
   * Checks the code. The session starts when `enter` is called, so a page can first show the
   * recovery codes of a sign-in that just turned two-step verification on.
   */
  verifySignIn: (challengeToken: string, code: string) => Promise<VerifiedSignIn>;
  signOut: () => Promise<void>;
  /** Re-reads the signed-in user after a change to their own account. */
  refreshUser: () => Promise<void>;
  can: (permission: Permission) => boolean;
}

export interface VerifiedSignIn {
  recoveryCodes?: string[];
  enter: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

export class WrongPanelError extends Error {}

export function PanelAuthProvider({
  api,
  audience,
  children,
}: {
  api: PanelApi;
  audience: PanelAudience;
  children: ReactNode;
}) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<Principal | null>(null);
  const [restoring, setRestoring] = useState(true);
  const [sessionLost, setSessionLost] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.onSessionLost = () => {
      queryClient.clear();
      setUser(null);
      setSessionLost(true);
    };
    api
      .restore()
      .then(async (p) => {
        if (cancelled) return;
        if (p && !principalFitsAudience(p, audience)) {
          await api.logout();
          p = null;
        }
        setUser(p);
      })
      .catch(() => !cancelled && setUser(null))
      .finally(() => !cancelled && setRestoring(false));
    return () => {
      cancelled = true;
    };
  }, [api, audience, queryClient]);

  const enter = useCallback(
    async (p: Principal): Promise<() => void> => {
      if (!principalFitsAudience(p, audience)) {
        await api.logout();
        throw new WrongPanelError();
      }
      return () => {
        queryClient.clear();
        setSessionLost(false);
        setUser(p);
      };
    },
    [api, audience, queryClient],
  );

  const signIn = useCallback(
    async (email: string, password: string) => {
      const result = await api.login(email, password);
      if ('challengeToken' in result) return result;
      (await enter(result))();
      return result;
    },
    [api, enter],
  );

  const verifySignIn = useCallback(
    async (challengeToken: string, code: string) => {
      const { user: p, recoveryCodes } = await api.verifyLogin(challengeToken, code);
      return { recoveryCodes, enter: await enter(p) };
    },
    [api, enter],
  );

  const signOut = useCallback(async () => {
    await api.logout();
    queryClient.clear();
    setUser(null);
  }, [api, queryClient]);

  const refreshUser = useCallback(async () => {
    setUser(await api.me());
  }, [api]);

  const can = useCallback((perm: Permission) => (user?.permissions ?? []).includes(perm), [user]);

  const value = useMemo<AuthState>(
    () => ({
      api,
      audience,
      user,
      restoring,
      sessionLost,
      signIn,
      verifySignIn,
      signOut,
      refreshUser,
      can,
    }),
    [api, audience, user, restoring, sessionLost, signIn, verifySignIn, signOut, refreshUser, can],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <PanelAuthProvider>');
  return ctx;
}

export function useApi(): PanelApi {
  return useAuth().api;
}

/** Route guard: waits for session restore, then redirects anonymous visitors to /login. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { user, restoring } = useAuth();
  const location = useLocation();
  if (restoring) return <Spinner className="h-screen" />;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  return <>{children}</>;
}

/** Renders children only when the signed-in user holds the permission (UI hint; the API enforces). */
export function Can({
  permission,
  children,
  fallback = null,
}: {
  permission: Permission;
  children: ReactNode;
  fallback?: ReactNode;
}) {
  return useAuth().can(permission) ? <>{children}</> : <>{fallback}</>;
}
